/**
 * Login Component
 * 
 * Standalone login page with authentication - Exact UX design implementation
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(formData.email, formData.password, formData.rememberMe);
      navigate('/');
    } catch (error) {
      console.error('Login failed:', error);
      setError(error.response?.data?.detail || t('login.errorInvalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen md:flex-row md:h-screen overflow-hidden">
      {/* Mobile-only: compact header with gradient (replaces hero on xs) */}
      <header 
        className="sm:hidden flex items-center justify-center gap-3 py-4 px-4 shrink-0"
        style={{ background: 'linear-gradient(135deg, #007E8F 0%, #00C6B3 100%)' }}
      >
        <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
          <i className="fa-solid fa-robot text-white text-xl"></i>
        </div>
        <div>
          <h1 className="text-lg font-bold text-white">{t('login.hero.title')}</h1>
          <p className="text-white/80 text-xs">{t('login.hero.subtitle')}</p>
        </div>
      </header>
      {/* Left Panel - Hero (hidden on xs, visible from sm) */}
      <section 
        className="hidden sm:flex flex-1 flex-col items-center justify-center p-4 sm:p-6 md:p-12 relative overflow-hidden min-h-[200px] md:min-h-0"
        style={{
          background: 'linear-gradient(135deg, #007E8F 0%, #00C6B3 50%, #6FFFE0 100%)'
        }}
      >
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-10 left-10 w-32 h-32 md:top-20 md:left-20 md:w-64 md:h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-48 h-48 md:bottom-20 md:right-20 md:w-96 md:h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 max-w-xl w-full">
          <div className="mb-4 sm:mb-6 md:mb-12">
            <div className="flex items-center space-x-3 sm:space-x-4 mb-4 md:mb-8">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl flex items-center justify-center shadow-2xl shrink-0">
                <i className="fa-solid fa-robot text-white text-2xl sm:text-3xl"></i>
              </div>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white truncate">{t('login.hero.title')}</h1>
                <p className="text-white/80 text-xs sm:text-sm truncate">{t('login.hero.subtitle')}</p>
              </div>
            </div>
          </div>
          <div className="mb-4 sm:mb-6 md:mb-12">
            <p className="text-sm sm:text-base md:text-xl text-white/90 mb-4 md:mb-8 leading-relaxed">{t('login.hero.description')}</p>
            <div className="space-y-3 sm:space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center space-x-3 sm:space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl flex items-center justify-center shrink-0">
                    <i className={`fa-solid ${i === 1 ? 'fa-bolt' : i === 2 ? 'fa-brain' : 'fa-shield-halved'} text-white text-base sm:text-xl`}></i>
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-white font-bold text-sm sm:text-lg">{t(`login.hero.feature${i}Title`)}</h3>
                    <p className="text-white/80 text-xs sm:text-sm">{t(`login.hero.feature${i}Desc`)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-4 md:gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 border border-white/20">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-0.5 sm:mb-2">15+</div>
              <div className="text-white/80 text-xs sm:text-sm">{t('login.hero.stat1')}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 border border-white/20">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-0.5 sm:mb-2">∞</div>
              <div className="text-white/80 text-xs sm:text-sm">{t('login.hero.stat2')}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 border border-white/20">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-0.5 sm:mb-2">24/7</div>
              <div className="text-white/80 text-xs sm:text-sm">{t('login.hero.stat3')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Right Panel - Login Form (full width on mobile, fixed width on md+) */}
      <section className="w-full md:w-[600px] md:min-w-[400px] md:max-w-[600px] flex-1 flex items-center justify-center bg-white shadow-2xl overflow-y-auto md:shrink-0">
        <div className="w-full max-w-md py-6 px-4 sm:py-8 sm:px-6 md:py-12 md:px-8">
          {/* Login Header */}
          <div className="mb-6 sm:mb-8 md:mb-10 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#212121] mb-2 md:mb-3">{t('login.title')}</h2>
            <p className="text-[#4A4A4A] text-sm sm:text-base md:text-lg">{t('login.subtitle')}</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl">
              <div className="flex items-center">
                <i className="fas fa-exclamation-circle text-red-600 mr-3"></i>
                <p className="text-sm text-red-600 font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label className="block mb-2">
                <span className="text-sm font-bold text-[#212121]">{t('login.email')} <span className="text-[#00C6B3]">*</span></span>
              </label>
              <div className="relative">
                <i className="fa-solid fa-envelope absolute left-3 top-1/2 -translate-y-1/2 text-[#B0B9C1]"></i>
                <input 
                  type="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  className="w-full pl-11 pr-4 py-3.5 sm:py-4 border-2 border-[#B0B9C1]/30 rounded-xl focus:ring-2 focus:ring-[#00C6B3] focus:border-transparent text-[#212121] placeholder-[#B0B9C1] transition-all text-base min-h-[48px]"
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block mb-2">
                <span className="text-sm font-bold text-[#212121]">{t('login.password')} <span className="text-[#00C6B3]">*</span></span>
              </label>
              <div className="relative">
                <i className="fa-solid fa-lock absolute left-3 top-1/2 -translate-y-1/2 text-[#B0B9C1]"></i>
                <input 
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={t('login.password')}
                  className="w-full pl-11 pr-12 py-3.5 sm:py-4 border-2 border-[#B0B9C1]/30 rounded-xl focus:ring-2 focus:ring-[#00C6B3] focus:border-transparent text-[#212121] placeholder-[#B0B9C1] transition-all text-base min-h-[48px]"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B0B9C1] hover:text-[#00C6B3] p-1 touch-manipulation"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
            </div>

            {/* Remember & Forgot */}
            <div className="flex items-center justify-between">
              <label className="flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  className="w-5 h-5 text-[#00C6B3] border-[#B0B9C1]/30 rounded focus:ring-[#00C6B3] cursor-pointer"
                />
                <span className="ml-3 text-sm font-medium text-[#212121]">{t('login.rememberMe')}</span>
              </label>
              <a href="#" className="text-sm font-semibold text-[#00C6B3] hover:text-[#007E8F] transition-colors">
                {t('login.forgotPassword')}
              </a>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              disabled={loading}
              className="w-full py-3.5 sm:py-4 min-h-[48px] hover:opacity-90 text-white rounded-xl text-base sm:text-lg font-bold shadow-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
              style={{
                background: 'linear-gradient(135deg, #007E8F 0%, #00C6B3 100%)'
              }}
            >
              <i className="fa-solid fa-right-to-bracket mr-3"></i>
              {loading ? t('login.signingIn') : t('login.signIn')}
            </button>

            {/* Divider */}
            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#B0B9C1]/30"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-[#4A4A4A] font-medium">{t('login.orContinueWith')}</span>
              </div>
            </div>

            {/* Social Login */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4">
              <button 
                type="button" 
                className="min-h-[48px] py-3 bg-white border-2 border-[#B0B9C1]/30 hover:border-[#00C6B3] hover:bg-[#F5F6F8] rounded-xl transition-all flex items-center justify-center touch-manipulation"
              >
                <i className="fa-brands fa-google text-lg sm:text-xl text-[#4A4A4A]"></i>
              </button>
              <button 
                type="button" 
                className="min-h-[48px] py-3 bg-white border-2 border-[#B0B9C1]/30 hover:border-[#00C6B3] hover:bg-[#F5F6F8] rounded-xl transition-all flex items-center justify-center touch-manipulation"
              >
                <i className="fa-brands fa-github text-lg sm:text-xl text-[#4A4A4A]"></i>
              </button>
              <button 
                type="button" 
                className="min-h-[48px] py-3 bg-white border-2 border-[#B0B9C1]/30 hover:border-[#00C6B3] hover:bg-[#F5F6F8] rounded-xl transition-all flex items-center justify-center touch-manipulation"
              >
                <i className="fa-brands fa-microsoft text-lg sm:text-xl text-[#4A4A4A]"></i>
              </button>
            </div>
          </form>

          {/* Signup Link */}
          <div className="mt-6 sm:mt-8 text-center">
            <p className="text-[#4A4A4A] text-sm sm:text-base">
              {t('login.noAccount')}{' '}
              <Link to="/register" className="font-bold text-[#00C6B3] hover:text-[#007E8F] transition-colors underline focus:outline-none focus:ring-2 focus:ring-[#00C6B3] rounded">
                {t('login.createAccount')}
              </Link>
            </p>
          </div>

          {/* Security Badge */}
          <div className="mt-6 sm:mt-10 p-3 sm:p-4 bg-[#F5F6F8] rounded-xl border border-[#B0B9C1]/20">
            <div className="flex items-center justify-center space-x-3 text-sm text-[#4A4A4A]">
              <i className="fa-solid fa-shield-halved text-[#00C6B3]"></i>
              <span>{t('login.securityBadge')}</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

